import { useTranslations } from '@vocab/react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Column,
  Columns,
  Inline,
  Notice,
  Spread,
  Stack,
  Text,
} from 'braid-design-system';

import translations from '@seek/rr-caja/src/.vocab';

import { IconSeekPass } from '../Icons';
import { IconSeekPassSmall } from '../Icons/IconSeekPassSmall/IconSeekPassSmall';
import IconVerifyBoostSVG from '../Icons/IconVerifyBoost/IconVerifyBoostSVG';

type VerifyBoostProps = {
  id: string;
  onButtonClick: () => void;
  isVBEnabled: boolean;
  isError: boolean;
};

const getBorderColor = (isError: boolean, isVBEnabled: boolean) => {
  if (isError) {
    return 'borderCritical';
  }
  if (isVBEnabled) {
    return 'borderFormAccent';
  }
  return 'borderNeutralLight';
};

export const VerifyBoost = ({
  id,
  isVBEnabled,
  onButtonClick,
  isError,
}: VerifyBoostProps) => {
  const { t } = useTranslations(translations);
  return (
    <Stack space="medium">
      <Box
        id={id}
        boxShadow={getBorderColor(isError, isVBEnabled)}
        borderRadius="large"
        paddingY="medium"
        paddingX="medium"
      >
        <Columns space="medium" collapseBelow="desktop" reverse>
          <Column width="1/3">
            <Box
              display="flex"
              padding="large"
              borderRadius="large"
              background="formAccentSoft"
              justifyContent="center"
              alignItems="center"
              height="full"
            >
              <IconVerifyBoostSVG className="" />
            </Box>
          </Column>
          <Column>
            <Spread direction="vertical" space="large">
              <Stack space="small">
                <Badge tone="positive">{t('verifyboost.badge')}</Badge>

                <Text weight="strong" size="large">
                  {t('verifyboost.heading')}
                </Text>

                <Box marginTop="xxsmall">
                  <Text size="standard">
                    {t('verifyboost.description.item1')}
                    {'\u00A0'}
                    <IconSeekPassSmall />
                  </Text>
                </Box>
                <Text size="standard">
                  {t('verifyboost.description.item2')}
                </Text>

                <Text size="standard">
                  {t('verifyboost.description.item3')}
                </Text>
              </Stack>
              <Columns space="small" collapseBelow="desktop">
                <Column width="content">
                  {!isVBEnabled ? (
                    <Button
                      tone="formAccent"
                      variant="ghost"
                      onClick={onButtonClick}
                    >
                      {t('verifyboost.requestButton')}
                    </Button>
                  ) : (
                    <Button tone="formAccent" onClick={onButtonClick}>
                      {'- '} {t('verifyboost.removeButton')}
                    </Button>
                  )}
                </Column>
              </Columns>
            </Spread>
          </Column>
        </Columns>
      </Box>

      {isVBEnabled && isError && (
        <Stack space="medium">
          <Notice tone="critical">
            <Inline space="xxsmall" alignY="center">
              <Text>{t('verifyboost.error.part1')}</Text>
              <Text size="small">
                <IconSeekPass />
              </Text>
              <Text>{t('verifyboost.error.part2')}</Text>
            </Inline>
          </Notice>
        </Stack>
      )}

      {isVBEnabled && !isError && (
        <Alert tone="promote">
          <Text tone="promote">
            {t('verifyboost.message')} <IconSeekPassSmall />
          </Text>
        </Alert>
      )}
    </Stack>
  );
};
